import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Payment, PaymentDetailsResponse } from '../../types/subscription';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import { CreditCard, QrCode, AlertCircle, FileText, Receipt, ExternalLink } from 'lucide-react';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import api from '../../lib/api';
import LoadingSpinner from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';
import { formatDate } from '../../lib/formatters';

interface PaymentHistoryProps {
  payments: Payment[];
}

function PaymentHistory({ payments }: PaymentHistoryProps) {
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [initialPaymentStatus, setInitialPaymentStatus] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch payment details when a payment is selected
  const { data: paymentDetails, isLoading: isLoadingDetails } = useQuery<PaymentDetailsResponse>({
    queryKey: ['payment-details', selectedPaymentId],
    queryFn: async () => {
      if (!selectedPaymentId) throw new Error('No payment selected');
      const response = await api.get(`/subscriptions/payments/${selectedPaymentId}`);
      return response.data;
    },
    enabled: !!selectedPaymentId,
  });

  // Store initial payment status when modal opens
  useEffect(() => {
    if (selectedPaymentId && paymentDetails?.data && !initialPaymentStatus) {
      setInitialPaymentStatus(paymentDetails.data.payment_status);
    } else if (!selectedPaymentId) {
      setInitialPaymentStatus(null);
    }
  }, [selectedPaymentId, paymentDetails?.data, initialPaymentStatus]);

  // Poll for payment status updates when modal is open
  useEffect(() => {
    if (!selectedPaymentId) return;

    const checkPaymentStatus = async () => {
      try {
        const response = await api.get(`/subscriptions/payments/${selectedPaymentId}`);
        const payment = response.data?.data;

        // Only close modal if payment status changed from pending to paid
        if (payment?.payment_status === 'paid' && initialPaymentStatus === 'pending') {
          setSelectedPaymentId(null);
          queryClient.invalidateQueries({ queryKey: ['payments'] });
          toast.success('Pagamento confirmado com sucesso!');
        }

        queryClient.setQueryData(['payment-details', selectedPaymentId], response.data);
      } catch (error) {
        console.error('Error checking payment status:', error);
      }
    };

    // Check immediately and then every 5 seconds
    checkPaymentStatus();
    const interval = setInterval(checkPaymentStatus, 5000);

    return () => clearInterval(interval);
  }, [selectedPaymentId, queryClient, initialPaymentStatus]);

  const getPaymentStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'refunded':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatCurrency = (value: string) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(parseFloat(value));
  };

  const handleCopyPix = async (pixPayload: string) => {
    try {
      await navigator.clipboard.writeText(pixPayload);
      toast.success('Código PIX copiado!');
    } catch (error) {
      toast.error('Erro ao copiar código PIX');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Pagamentos</CardTitle>
        </CardHeader>
        <CardContent>
          {payments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Data
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Valor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Método
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(payment.due_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={getPaymentStatusBadgeVariant(payment.payment_status)}>
                          {payment.payment_status === 'paid' ? 'Pago' :
                           payment.payment_status === 'pending' ? 'Pendente' :
                           payment.payment_status === 'failed' ? 'Falhou' : 'Reembolsado'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {payment.payment_method}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center gap-2">
                          {(payment.pix_id || payment.pdf || payment.invoice || payment.barcode) && (
                            <Button
                              variant="ghost"
                              onClick={() => setSelectedPaymentId(payment.id)}
                              title="Ver Detalhes"
                            >
                              <ExternalLink className="h-5 w-5 text-primary-600" />
                            </Button>
                          )}
                          {payment.pdf && (
                            <Button
                              variant="ghost"
                              onClick={() => window.open(payment.pdf, '_blank')}
                              title="Abrir Boleto"
                            >
                              <FileText className="h-5 w-5 text-blue-600" />
                            </Button>
                          )}
                          {payment.invoice && (
                            <Button
                              variant="ghost"
                              onClick={() => window.open(payment.invoice, '_blank')}
                              title="Ver Nota Fiscal"
                            >
                              <Receipt className="h-5 w-5 text-green-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-6">
              <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum pagamento encontrado</h3>
              <p className="mt-1 text-sm text-gray-500">
                Não há registros de pagamentos no momento.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Details Modal */}
      <Modal
        isOpen={!!selectedPaymentId}
        onClose={() => setSelectedPaymentId(null)}
        title="Detalhes do Pagamento"
      >
        {isLoadingDetails ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="lg" />
          </div>
        ) : paymentDetails?.data ? (
          <div className="flex flex-col items-center space-y-4">
            {paymentDetails.data.pix_qr_code_url ? (
              <img
                src={paymentDetails.data.pix_qr_code_url}
                alt="QR Code PIX"
                className="w-64 h-64"
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-8">
                <AlertCircle className="h-12 w-12 text-yellow-500 mb-2" />
                <p className="text-gray-600">QR Code não disponível no momento.</p>
              </div>
            )}

            {paymentDetails.data.pix_payload && (
              <div className="w-full">
                <p className="text-sm text-gray-500 mb-2">PIX Copia e Cola:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paymentDetails.data.pix_payload}
                    readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-50 text-sm"
                  />
                  <Button
                    onClick={() => handleCopyPix(paymentDetails.data.pix_payload!)}
                    variant="secondary"
                  >
                    Copiar
                  </Button>
                </div>
              </div>
            )}

            {paymentDetails.data.barcode && (
              <div className="w-full">
                <p className="text-sm text-gray-500 mb-2">Código de Barras do Boleto:</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={paymentDetails.data.barcode}
                    readOnly
                    className="w-full px-3 py-2 border rounded-md bg-gray-50 text-sm font-mono"
                  />
                  <Button
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(paymentDetails.data.barcode!);
                        toast.success('Código de barras copiado!');
                      } catch (error) {
                        toast.error('Erro ao copiar código de barras');
                      }
                    }}
                    variant="secondary"
                  >
                    Copiar
                  </Button>
                </div>
              </div>
            )}

            <div className="w-full flex gap-2">
              {paymentDetails.data.pdf && (
                <Button
                  onClick={() => window.open(paymentDetails.data.pdf, '_blank')}
                  variant="primary"
                  className="flex-1"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Abrir Boleto
                </Button>
              )}
              {paymentDetails.data.invoice && (
                <Button
                  onClick={() => window.open(paymentDetails.data.invoice, '_blank')}
                  variant="secondary"
                  className="flex-1"
                >
                  <Receipt className="h-4 w-4 mr-2" />
                  Ver Nota Fiscal
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <AlertCircle className="h-12 w-12 text-red-500 mb-2" />
            <p className="text-gray-600">Não foi possível carregar os detalhes do pagamento.</p>
          </div>
        )}
      </Modal>
    </>
  );
}

export default PaymentHistory;